import type { ReactNode } from 'react';
import { PlatformHeader } from '../../../shared/platform/PlatformHeader';
import { TitleBackdrop } from '../../../shared/platform/TitleBackdrop';
import { Icon } from '../../../shared/ui/Primitives';
import type { WorkspaceProjection } from '../domain/projection';

function Section({
  id,
  title,
  english,
  icon,
  children,
  className = '',
}: {
  readonly id: string;
  readonly title: string;
  readonly english: string;
  readonly icon: string;
  readonly children: ReactNode;
  readonly className?: string;
}) {
  return (
    <section id={id} className={`mw-card ${className}`} aria-labelledby={`${id}-title`}>
      <h2 id={`${id}-title`}>
        <span className="mw-section-icon">
          <Icon name={icon} />
        </span>
        {title}
        <span>{english}</span>
      </h2>
      {children}
    </section>
  );
}

function Boundaries() {
  return (
    <div className="mw-boundaries" data-workspace-boundaries>
      <span>SYNTHETIC</span>
      <span>NON-OPERATIONAL</span>
      <span>NON-AUTHORITY</span>
      <small>合成内容 · 非行动用途 · 该读取结果不构成授权</small>
    </div>
  );
}

/** Takes presentation projection only; never receives Backend DTOs or fetches data. */
export function MissionWorkspaceView({ state }: { readonly state: WorkspaceProjection }) {
  return (
    <>
      <PlatformHeader activePage="workspace" />
      <div className="mw">
        <div className="p01-titlebar mw-titlebar">
          <TitleBackdrop />
          <h1>
            救援任务工作台 <span>Mission Workspace</span>
          </h1>
          <p>
            基于场景知识的只读上下文 <span>AI for Safer Cities and People</span>
          </p>
        </div>
        <div className="mw-sticky">
          <div className="mw-statusline">
            <strong>{state.templateId}</strong>
            <span>Version {state.version}</span>
            <span data-workspace-status role="status">
              {state.status} · {state.statusText}
            </span>
          </div>
          <Boundaries />
        </div>
        <main className="mw-main">
          <div className="mw-context-strip">
            <div>
              <Icon name="file" />
              <span>
                Read Context<strong>MissionTemplate Context</strong>
              </span>
            </div>
            <div>
              <Icon name="layers" />
              <span>
                Template Version<strong>{state.version}</strong>
              </span>
            </div>
            <div>
              <Icon name="clock" />
              <span>
                Source Time<strong>当前读取范围未提供时间参考</strong>
              </span>
            </div>
            <div>
              <Icon name="book" />
              <span>
                Source<strong>{state.identityLabel}</strong>
              </span>
            </div>
          </div>
          <Section
            id="mw-context"
            title="任务上下文"
            english="Mission Context"
            icon="building"
            className="mw-context"
          >
            <div className="mw-hero-grid">
              <div className="mw-template">
                <p className="mw-eyebrow">{state.identityLabel} · Template Identity</p>
                <h3>{state.templateId}</h3>
                <p>MissionTemplate · Version {state.version}</p>
                <p className="mw-muted">当前页面展示一个精确模板的读取上下文。</p>
                <dl className="mw-meta">
                  <div>
                    <dt>Kind</dt>
                    <dd>MissionTemplate</dd>
                  </div>
                  <div>
                    <dt>Schema</dt>
                    <dd>{state.schema}</dd>
                  </div>
                </dl>
                <p className="mw-scope">Mission identity unavailable · 任务身份来源不可用</p>
              </div>
              <figure className="mw-illustration">
                <div
                  className="mw-illustration-image"
                  role="img"
                  aria-label="V6 设计中的高层建筑场景示意图，仅为示例，不表示现场或资源可用状态"
                />
                <figcaption>Scenario Illustration · 设计示例，非现场</figcaption>
              </figure>
              <div className="mw-template-summary">
                <h3>模板上下文</h3>
                <p>内容说明、阶段与依赖的工作台结构化映射尚未建立。</p>
                <div className="mw-definition-tags">
                  <span>Context Description</span>
                  <span>Stages · 定义</span>
                  <span>Dependencies · 声明</span>
                </div>
                <p className="mw-muted">
                  Candidate delivery / projection 尚未建立。模板定义不代表资源分配。
                </p>
                <p className="mw-scope">Relationship Unknown · 关系未知，不代表未绑定</p>
              </div>
            </div>
          </Section>
          <div className="mw-grid">
            <Section id="mw-focus" title="当前关注" english="Current Focus" icon="search">
              <p className="mw-section-note">阅读关注方向 · Presentation Projection</p>
              <div className="mw-focus-grid">
                {[
                  ['search', '当前任务目标', '当前读取范围未提供目标的工作台映射。'],
                  ['layers', '场景关键要素', '模板阶段与依赖仅为来源声明，映射尚未建立。'],
                  ['alert', '核心关注', '来源缺口保持未知，不推导优先级或完成程度。'],
                ].map(([icon, title, text], i) => (
                  <article key={title} className={`mw-focus mw-focus-${i}`}>
                    <Icon name={icon ?? 'info'} />
                    <h3>{title}</h3>
                    <p>{text}</p>
                  </article>
                ))}
              </div>
            </Section>
            <Section id="mw-unknown" title="关键信息缺口" english="Critical Unknowns" icon="info">
              <p className="mw-section-note">消费边界披露 · 非来源 Unknown 完整清单</p>
              <div className="mw-unknown-notice">
                <strong>UNKNOWN · 信息尚未明确</strong>
                <p>Unknown ≠ Error。未知信息披露当前不可读取。</p>
              </div>
              <dl className="mw-disclosure">
                <div>
                  <dt>Unknown Item</dt>
                  <dd>结构化 Unknown 的工作台映射</dd>
                </div>
                <div>
                  <dt>Reason</dt>
                  <dd>Consumer Alignment 尚未确立字段与 occurrence 映射。</dd>
                </div>
                <div>
                  <dt>Impact</dt>
                  <dd>不能填充可信条目、合并来源声明或推断没有未知项。</dd>
                </div>
                <div>
                  <dt>Status / Owner</dt>
                  <dd>来源元信息当前不可读取；不新增解决状态或指派责任人。</dd>
                </div>
                <div>
                  <dt>Need Confirmation</dt>
                  <dd>来源确认事项或责任人尚未明确。</dd>
                </div>
              </dl>
            </Section>
            <Section id="mw-analysis" title="AI辅助分析" english="AI Analysis" icon="network">
              <p className="mw-section-note">Analysis · Not Authorization</p>
              <div className="mw-unavailable">
                <strong>当前读取范围未提供</strong>
                <p>Analysis / Context：没有已确立来源的分析内容。</p>
                <p>Limitation：不生成建议、决定或执行指令。</p>
              </div>
            </Section>
            <Section id="mw-questions" title="待确认问题" english="Pending Questions" icon="user">
              <p className="mw-section-note">人工判断问题 · 只读展示</p>
              <div className="mw-unavailable">
                <strong>当前读取范围未提供</strong>
                <p>Question / Context / Status：尚无可消费的问题来源。</p>
                <p>不表示无需判断；不会从缺口自动创建确认事项。</p>
              </div>
            </Section>
          </div>
          <Section id="mw-support" title="支持信息" english="Supporting Information" icon="report">
            <div className="mw-support-grid">
              <article>
                <h3>
                  <Icon name="shield" />
                  读取校验 <span>Read Verification</span>
                </h3>
                <strong className="mw-verification">
                  {state.status} · {state.statusText}
                </strong>
                <p>VERIFIED 仅核验精确对象的读取边界，不代表就绪、事实成立或依赖闭合。</p>
                <p>Incident Evidence：当前读取范围未提供。</p>
                <p>时间参考：当前读取范围未提供；读取成功不证明内容最新。</p>
              </article>
              <article>
                <h3>
                  <Icon name="book" />
                  来源参考 <span>Source Reference</span>
                </h3>
                <p>
                  {state.identityLabel} · {state.templateId}
                </p>
                <p>
                  Version {state.version} · {state.schema}
                </p>
                <p>页面限制说明来源：Consumer Alignment v1.0 / P2-MC-07。</p>
                <a href="#mw-exact-identity">查看完整精确身份 ↓</a>
              </article>
              <article>
                <h3>
                  <Icon name="shield" />
                  边界快照 <span>Boundary Snapshot</span>
                </h3>
                <Boundaries />
                <p>MissionTemplate ≠ Mission Runtime</p>
                <p>Definition ≠ Assignment</p>
                <p>Analysis ≠ Authorization</p>
              </article>
            </div>
            <details id="mw-exact-identity" className="mw-details">
              <summary>完整精确身份与来源说明</summary>
              <dl className="mw-identity">
                {state.identity.map(({ label, value }) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
              <p>
                六项身份共同限定一个请求，不自动换版或混用来源。尚未建立的结构化映射保持不可用。
              </p>
            </details>
            {state.sourceText !== null && (
              <details className="mw-details">
                <summary>已核验快照原文（来源声明，非工作台语义映射）</summary>
                <p>保留来源声明与 Unknown 的完整原文；不推导阶段状态、授权或资源分配。</p>
                <pre data-workspace-source>{state.sourceText}</pre>
              </details>
            )}
          </Section>
        </main>
        <footer className="mw-footer">
          <strong>AI消防+救援</strong>
          <span>AI Fire + Rescue</span>
          <span>Mission Workspace · Read Slice · NON-AUTHORITY</span>
        </footer>
      </div>
    </>
  );
}
